import { LEARNING_STATES } from '@constants/learningStates.ts';
import { ROUTES } from '@router/constants.ts';
import { EVALUATION_METHOD_PILL_LABELS, TASK_SCALE_TYPE } from '@routes/sidebar/___shared___/constants.ts';
import type { EvaluationMethod, TaskScaleType } from '@routes/sidebar/___shared___/types.ts';
import type { LearningOverviewResponse } from '@routes/sidebar/learning/___pages___/learning-overview/api/learningOverview.api';
import type { LearningResponse } from '@routes/sidebar/learning/api/learning.api.ts';
import { translateItem } from '@utils/translations.ts';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export const getEvaluationMethodPillLabel = (evaluationMethod: EvaluationMethod | null): string | null => {
    if (evaluationMethod === null) {
        return null;
    }

    return translateItem(EVALUATION_METHOD_PILL_LABELS[evaluationMethod]);
};

interface LearningStatus {
    isCompleted: boolean;
    isUpdated: boolean;
    isRefresher: boolean;
    isOverdue: boolean;
    isDueSoon: boolean;
    isRedo: boolean;
}

const isServerNowDaysLaterThanOrEqual = (utcDateTime: string, serverTime: Date, days = 0): boolean => {
    return dayjs.utc(serverTime).startOf('day').diff(dayjs.utc(utcDateTime).startOf('day'), 'day') >= days;
};

export const getLearningStatus = (
    learning: LearningResponse | LearningOverviewResponse | null,
    serverTime: Date,
): LearningStatus => {
    if (learning === null) {
        return {
            isCompleted: false,
            isUpdated: false,
            isRefresher: false,
            isOverdue: false,
            isDueSoon: false,
            isRedo: false,
        };
    }
    const userActivity = learning.userActivity;
    const isCompleted = !!userActivity?.completedAtUTC;
    const isUpdated = !!userActivity?.states?.includes(LEARNING_STATES.Updated);
    const isRefresher = !!userActivity?.states?.includes(LEARNING_STATES.Refresher);
    const isOverdue = !!userActivity?.states?.includes(LEARNING_STATES.Overdue);

    const isDueSoon =
        !isOverdue &&
        !!userActivity?.dueNoticeUTC &&
        isServerNowDaysLaterThanOrEqual(userActivity.dueNoticeUTC, serverTime);
    const isRedo = isCompleted && isRefresher;

    return {
        isCompleted,
        isUpdated,
        isRefresher,
        isOverdue,
        isDueSoon,
        isRedo,
    };
};

export const getTaskRoute = (
    pathwayId: string | number,
    pathwayItemId: string | number,
    taskRoute: string,
    isAssessorView = false,
) => {
    const root = isAssessorView ? ROUTES.SIDEBAR.ADMIN.ASSESSOR.ROOT : ROUTES.SIDEBAR.LEARNING;

    return `/${root}/task/${taskRoute}/${pathwayId}/${pathwayItemId}`;
};

export const getTaskScaleSize = (taskScaleType?: TaskScaleType): number =>
    taskScaleType === TASK_SCALE_TYPE.FivePointCompetence ? 5 : 2;
